import { INTERIM_EVENT_TYPE } from '../constants/interimEventTypes.js';

/** Contract entity types for escalate routing (lowercase). */
const ESCALATABLE_ENTITY_TYPES = new Set(['change_request', 'claim', 'truth']);

/**
 * Normalize a task's entityType to the escalate contract (truth|claim|change_request).
 * GS may return mixed casing (e.g. CLAIM); unknown types are not escalatable.
 * @param {string|undefined|null} entityType
 * @returns {'change_request'|'claim'|'truth'|null}
 */
export function normalizeEscalateEntityType(entityType) {
  if (typeof entityType !== 'string') return null;
  const normalized = entityType.trim().toLowerCase();
  return ESCALATABLE_ENTITY_TYPES.has(normalized) ? normalized : null;
}

/**
 * Resolve `{ entityType, entityUuid }` for an escalate call (CT-5094) from a
 * work-queue event. Escalate is entity-keyed — truth / claim / change_request
 * each have an escalate endpoint. Routing mirrors the beta Governance Studio
 * `MyWork.jsx`:
 *   - task              → its underlying entity (entityType + entityUuid)
 *   - change_request    → its changeRequestUuid
 *   - claim_suggestion  → its claimUuid (claim endpoint)
 *   - proposal          → its truthUuid ?? factUuid (truth endpoint; a proposal
 *                         has NO uuid of its own to escalate)
 * Returns `null` when the event has no escalatable entity (e.g. a proposal with
 * no truth yet), so callers can hide the action instead of firing a request that
 * would fail.
 *
 * @param {Object} event - a mapped work-queue event.
 * @returns {{ entityType: string, entityUuid: string }|null}
 */
export function escalateParamsFromEvent(event) {
  const detail = event?.detail ?? {};
  switch (event?.type) {
    case INTERIM_EVENT_TYPE.TASK: {
      // A task's entityType is the escalatable entity (truth|claim|change_request).
      const entityType = normalizeEscalateEntityType(detail.entityType);
      return entityType && detail.entityUuid
        ? { entityType, entityUuid: detail.entityUuid }
        : null;
    }
    case INTERIM_EVENT_TYPE.CHANGE_REQUEST:
      return detail.changeRequestUuid
        ? { entityType: 'change_request', entityUuid: detail.changeRequestUuid }
        : null;
    case INTERIM_EVENT_TYPE.CLAIM_SUGGESTION:
      return detail.claimUuid ? { entityType: 'claim', entityUuid: detail.claimUuid } : null;
    case INTERIM_EVENT_TYPE.PROPOSAL: {
      // A proposal escalates its underlying truth (never the proposalUuid).
      const truthUuid = detail.truthUuid ?? detail.factUuid;
      return truthUuid ? { entityType: 'truth', entityUuid: truthUuid } : null;
    }
    default:
      return null;
  }
}

/**
 * Map the modal's priority chip to the contract severity (Low|Medium|High|Critical).
 * `normal` returns `undefined` so the backend keeps its default severity.
 * @param {'normal'|'high'} priority
 * @returns {'High'|undefined}
 */
export function severityFromPriority(priority) {
  return priority === 'high' ? 'High' : undefined;
}
